import React, { useRef, useEffect, useState, useCallback } from 'react';

/* ═══════════════════════════════════════════════════════════════════
   SIGNATURE PAD — luxurious finger-signature canvas
   • Smooth ink with quadratic curves
   • Touch + mouse + pen support
   • RECORDS stroke timestamps for animated replay
   • Returns {dataUrl, strokes} on commit (strokes = array of strokes,
     each stroke = array of {x,y,t} relative to pad bounding-box width)
   ═══════════════════════════════════════════════════════════════════ */

const GOLD = '#D4AF37';

export const SignaturePad = ({
  height = 220,
  inkColor = GOLD,
  glow = true,
  onChange,
  placeholder = 'Sign here',
  disabled = false,
  testId = 'signature-pad',
}) => {
  const canvasRef = useRef(null);
  const wrapRef = useRef(null);
  const drawing = useRef(false);
  const lastPt = useRef(null);
  const strokesRef = useRef([]);          // [[{x,y,t}, ...], ...]
  const currentStrokeRef = useRef(null);
  const startTimeRef = useRef(null);
  const sizeRef = useRef({ w: 0, h: 0 });
  const [hasInk, setHasInk] = useState(false);

  const setupCanvas = useCallback(() => {
    const cv = canvasRef.current;
    const wrap = wrapRef.current;
    if (!cv || !wrap) return;
    const ratio = window.devicePixelRatio || 1;
    const w = wrap.clientWidth;
    // When `height` is null/'auto'/'100%' we measure the wrapper instead.
    const useFlex = height == null || height === 'auto' || height === '100%';
    const h = useFlex ? Math.max(160, wrap.clientHeight) : height;
    cv.width = Math.floor(w * ratio);
    cv.height = Math.floor(h * ratio);
    cv.style.width = `${w}px`;
    cv.style.height = `${h}px`;
    sizeRef.current = { w, h };
    const ctx = cv.getContext('2d');
    ctx.scale(ratio, ratio);
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = inkColor;
    ctx.lineWidth = 2.4;
    if (glow) {
      ctx.shadowColor = inkColor;
      ctx.shadowBlur = 8;
    }
  }, [height, inkColor, glow]);

  useEffect(() => {
    setupCanvas();
    const wrap = wrapRef.current;
    if (!wrap) return;
    const onResize = () => setupCanvas();
    window.addEventListener('resize', onResize);
    let ro;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(() => setupCanvas());
      ro.observe(wrap);
    }
    return () => {
      window.removeEventListener('resize', onResize);
      if (ro) ro.disconnect();
    };
  }, [setupCanvas]);

  const getPoint = (e) => {
    const cv = canvasRef.current;
    const rect = cv.getBoundingClientRect();
    const t = e.touches ? e.touches[0] : e;
    return { x: t.clientX - rect.left, y: t.clientY - rect.top };
  };

  const start = (e) => {
    if (disabled) return;
    e.preventDefault();
    drawing.current = true;
    lastPt.current = getPoint(e);
    if (startTimeRef.current == null) startTimeRef.current = performance.now();
    currentStrokeRef.current = [{
      x: lastPt.current.x,
      y: lastPt.current.y,
      t: performance.now() - startTimeRef.current,
    }];
    const ctx = canvasRef.current.getContext('2d');
    ctx.beginPath();
    ctx.moveTo(lastPt.current.x, lastPt.current.y);
  };

  const move = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const pt = getPoint(e);
    const ctx = canvasRef.current.getContext('2d');
    const mid = { x: (lastPt.current.x + pt.x) / 2, y: (lastPt.current.y + pt.y) / 2 };
    ctx.quadraticCurveTo(lastPt.current.x, lastPt.current.y, mid.x, mid.y);
    ctx.stroke();
    lastPt.current = pt;
    currentStrokeRef.current.push({
      x: pt.x, y: pt.y,
      t: performance.now() - startTimeRef.current,
    });
    if (!hasInk) setHasInk(true);
  };

  const end = () => {
    if (!drawing.current) return;
    drawing.current = false;
    if (currentStrokeRef.current && currentStrokeRef.current.length > 1) {
      strokesRef.current.push(currentStrokeRef.current);
    }
    currentStrokeRef.current = null;
    if (onChange && hasInk) {
      const { w, h } = sizeRef.current;
      onChange(canvasRef.current.toDataURL('image/png'), {
        strokes: strokesRef.current,
        width: w,
        height: h,
      });
    }
  };

  const clear = () => {
    const cv = canvasRef.current;
    if (!cv) return;
    const ctx = cv.getContext('2d');
    ctx.clearRect(0, 0, cv.width, cv.height);
    strokesRef.current = [];
    startTimeRef.current = null;
    setHasInk(false);
    if (onChange) onChange(null, null);
  };

  // When height prop is null/'auto'/'100%' we let the canvas wrapper fill its parent.
  const useFlex = height == null || height === 'auto' || height === '100%';

  return (
    <div
      className={useFlex ? 'w-full h-full flex flex-col' : 'w-full'}
      data-testid={testId}
    >
      <div
        ref={wrapRef}
        className={`relative w-full rounded-xl overflow-hidden ${useFlex ? 'flex-1 min-h-0' : ''}`}
        style={{
          height: useFlex ? '100%' : height,
          minHeight: useFlex ? 200 : undefined,
          background:
            'linear-gradient(180deg, rgba(255,255,255,0.04), rgba(0,0,0,0.55))',
          border: `1.2px solid ${GOLD}55`,
          boxShadow: `inset 0 0 28px rgba(212,175,55,0.06)`,
        }}
      >
        {/* Faint guide line */}
        <div
          className="absolute left-6 right-6 pointer-events-none"
          style={{
            top: `calc(50% + 22px)`,
            height: 1,
            background:
              'linear-gradient(90deg, transparent, rgba(212,175,55,0.35), transparent)',
          }}
        />

        {!hasInk && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <span
              className="font-['Cormorant_Garamond'] italic"
              style={{ color: 'rgba(212,175,55,0.45)', fontSize: 22 }}
            >
              {placeholder}
            </span>
          </div>
        )}

        <canvas
          ref={canvasRef}
          className="absolute inset-0 touch-none"
          onMouseDown={start}
          onMouseMove={move}
          onMouseUp={end}
          onMouseLeave={end}
          onTouchStart={start}
          onTouchMove={move}
          onTouchEnd={end}
          data-testid={`${testId}-canvas`}
        />
      </div>

      <div className="flex items-center justify-between mt-2 flex-shrink-0">
        <span
          className="font-['Outfit'] text-[9px] uppercase tracking-[0.25em]"
          style={{ color: hasInk ? GOLD : 'rgba(255,255,255,0.25)' }}
        >
          {hasInk ? 'Sealed' : 'Awaiting signature'}
        </span>
        <button
          type="button"
          onClick={clear}
          disabled={!hasInk}
          className="font-['Outfit'] text-[10px] uppercase tracking-[0.2em] disabled:opacity-30"
          style={{ color: 'rgba(255,255,255,0.5)' }}
          data-testid={`${testId}-clear`}
        >
          Clear
        </button>
      </div>
    </div>
  );
};

export default SignaturePad;
